const moment = require('moment');
const _ = require('lodash');
const TANKS = require('../constants/tanks');

const fallbackTankId = TANKS.ROTH_275.key;

const Calculations = {
  getStatsForDevice: (device) => {
    if (!device || _.isUndefined(device.lastReadingDistance)) {
      return {
        gallonsRemaining: null,
        percentRemaining: null,
        fillCapacity: Calculations.getActualCapacity(device),
      };
    }
    const reading = { dist: device.lastReadingDistance };
    return {
      gallonsRemaining: Calculations.calculateRemainingGallons(reading, device),
      percentRemaining: Calculations.calculateRemainingPercent(reading, device),
      fillCapacity: Calculations.getActualCapacity(device),
    };
  },
  sortByTimeField: (field) => (a, b) => {
    if (a[field] && !b[field]) {
      return -1;
    } else if (b[field] && !a[field]) {
      return 1;
    }
    return moment(a[field]).isBefore(moment(b[field])) ? -1 : 1;
  },
  getTankOffset: (device, tank) => {
    if (_.isNumber(device.tankOffset)) {
      return device.tankOffset;
    }
    if (_.isNumber(tank.offset)) {
      return tank.offset;
    }
    return 0;
  },
  getTank: (device) => {
    const tankId = device.tankId;
    return TANKS[tankId] || TANKS[fallbackTankId];
  },
  getActualCapacity: (device) => {
    if (!device || !TANKS[device.tankId]) {
      return null;
    }
    const tank = TANKS[device.tankId];
    const capacity = device.actualCapacity || tank.actualCapacity;
    if (capacity > tank.gallons || !device.isTethered) {
      return capacity;
    }
    return capacity * 2;
  },
  calculateRemainingGallons: (reading = {}, device = {}) => {
    const { dist } = reading;
    if (_.isUndefined(dist)) {
      return '--';
    }
    const tank = Calculations.getTank(device);
    const isTethered = device.isTethered;
    const tankOffset = Calculations.getTankOffset(device, tank);
    const value = _.toNumber(tank.calculateGallonsRemaining(dist, tankOffset).toFixed(2));
    return isTethered ? value * 2 : value;
  },
  calculateRemainingPercent: (reading = {}, device = {}) => {
    const { dist } = reading;
    if (_.isUndefined(dist)) {
      return '--';
    }
    const actualCapacity = Calculations.getActualCapacity(device);
    const gallonsRemaining = Calculations.calculateRemainingGallons(
      reading,
      device,
    );
    if (gallonsRemaining > actualCapacity) {
      return 100;
    }
    return Math.round((gallonsRemaining * 100) / actualCapacity);
  },
  sortedFilteredReadings: (readings, cutoffDate, device) => {
    return readings
      .filter(
        (reading) =>
          !!reading.dist &&
          !!reading.createdAt &&
          moment(reading.createdAt).isAfter(cutoffDate, 'day'),
      )
      .sort(Calculations.sortByTimeField('createdAt'))
      .map((reading) => ({
        ...reading,
        created: moment(reading.createdAt).format('L'),
        date: moment(reading.createdAt).format('MM/DD/YYYY'),
        gallonsRemaining: Calculations.calculateRemainingGallons(
          reading,
          device,
        ),
        percentRemaining: Calculations.calculateRemainingPercent(
          reading,
          device,
        ),
      }));
  },
  readingsByDay: (sortedFilteredReadings) => {
    const yesterday = moment().subtract(1, 'day').startOf('day');
    const readingsByDay = _.map(
      _.groupBy(sortedFilteredReadings, 'created'),
      (readingsForDay, dateKey) => {
        // ensure we return the latest reading for recent dates
        if (moment(dateKey, 'L').isAfter(yesterday)) {
          const sortedReadingsForDay = readingsForDay
            .sort(Calculations.sortByTimeField('createdAt'))
            .reverse();
          return sortedReadingsForDay[0];
        }
        const mostGallonsRemainingReading = _.maxBy(
          readingsForDay,
          'gallonsRemaining',
        );
        const leastGallonsRemainingReading = _.minBy(
          readingsForDay,
          'gallonsRemaining',
        );
        if (mostGallonsRemainingReading - leastGallonsRemainingReading > 15) {
          return mostGallonsRemainingReading;
        }
        return leastGallonsRemainingReading;
      },
    );
    return readingsByDay.map((readingForDay, i) => {
      let consumption = 0;
      if (i > 0 && readingsByDay[i - 1]) {
        consumption =
          readingsByDay[i - 1].gallonsRemaining -
          readingForDay.gallonsRemaining;
      }
      return _.omit(
        {
          ...readingForDay,
          consumption,
          date: moment(readingForDay.createdAt).format('MM/DD/YYYY'),
        },
        ['updatedAt'],
      );
    });
  },
  calculateStats: (readings, cutoffDate, device) => {
    const sortedFilteredReadings = Calculations.sortedFilteredReadings(
      readings,
      cutoffDate,
      device,
    );
    const readingsByDay = Calculations.readingsByDay(
      sortedFilteredReadings,
    );
    const latestReading = readingsByDay[readingsByDay.length - 1];
    const totalConsumption = Math.round(
      _.sumBy(
        readingsByDay.filter(({ consumption }) => consumption > 0),
        'consumption',
      ),
    );
    const averageConsumption = Math.round(
      totalConsumption / readingsByDay.length,
    );
    const gallonsRemaining = Calculations.calculateRemainingGallons(
      latestReading,
      device,
    );
    const percentRemaining = Calculations.calculateRemainingPercent(
      latestReading,
      device,
    );
    const latestReadingDate = latestReading
      ? moment(new Date(latestReading.createdAt))
      : null;
    const actualCapacity = Calculations.getActualCapacity(device);
    let refillAmount =
      gallonsRemaining !== null ? actualCapacity - gallonsRemaining : '';
    if (_.isNumber(refillAmount) && refillAmount < 0) {
      refillAmount = 0;
    }
    return {
      averageConsumption,
      totalConsumption,
      gallonsRemaining,
      percentRemaining,
      latestReadingDate,
      readingsByDay,
      sortedFilteredReadings,
      refillAmount,
      actualCapacity,
    };
  },
  dataForGraphs: (readings, device, timeFrame, showConsumption, startDate) => {
    return new Promise((resolve) => {
      if (!showConsumption) {
        const data = Calculations.calculateStats(
          readings,
          startDate,
          device,
        );
        return resolve(data);
      }

      let readingsForPeriod;
      let cutoffDate = startDate;
      let data = Calculations.calculateStats(readings, cutoffDate, device);
      if (timeFrame === 7) {
        readingsForPeriod = data.readingsByDay;
      } else if (timeFrame === 30) {
        readingsForPeriod = _.values(
          _.groupBy(data.readingsByDay, (reading) =>
            moment(reading.createdAt).endOf('week').format('MM/DD/YYYY'),
          ),
        ).map((readingsByWeek) => ({
          date: moment(readingsByWeek[0].createdAt)
            .endOf('week')
            .format('MM/DD/YYYY'),
          consumption: _.sumBy(
            readingsByWeek.filter(({ consumption }) => consumption > 0),
            'consumption',
          ),
        }));
      } else if (timeFrame === 365) {
        readingsForPeriod = _.values(
          _.groupBy(data.readingsByDay, (reading) =>
            moment(reading.createdAt).endOf('month').format('MM/DD/YYYY'),
          ),
        ).map((readingsByMonth) => ({
          date: moment(readingsByMonth[0].createdAt)
            .endOf('month')
            .format('MM/DD/YYYY'),
          consumption: _.sumBy(
            readingsByMonth.filter(({ consumption }) => consumption > 0),
            'consumption',
          ),
        }));
      }
      data.readingsByDay = _.take(readingsForPeriod, 24);
      return resolve(data);
    });
  },
};
module.exports = Calculations;
